import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Fire, ArrowClockwise, Crown, EnvelopeSimple, Phone, MapPin, PaperPlaneTilt, MagicWand, X } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function HotLeads() {
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [days, setDays] = useState(7);
  const [busy, setBusy] = useState(false);
  const [composer, setComposer] = useState(null); // {lead, subject, body_text, body_html, loading, sending}

  const load = async (d = days) => {
    setBusy(true);
    try {
      const [r, s] = await Promise.all([
        api.get("/admin/hot-leads", { params: { days: d, min_intents: 1 } }),
        api.get("/admin/hot-leads/conversion-stats", { params: { days: 30 } }),
      ]);
      setData(r.data);
      setStats(s.data);
    } catch {
      setData({ items: [] });
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(days); /* eslint-disable-next-line */ }, []);

  const openComposer = async (lead) => {
    const defaultTo = lead.owner_email || lead.business_email || "";
    setComposer({ lead, to: defaultTo, subject: "", body_text: "", body_html: "", loading: true, sending: false });
    try {
      const r = await api.post("/admin/hot-leads/draft-email", {
        slug: lead.slug,
        intents: lead.intents,
        last_query: lead.last_query || "",
        is_claimed: lead.claimed,
      });
      setComposer((c) => c && { ...c, loading: false, subject: r.data.subject || "", body_text: r.data.body_text || "", body_html: r.data.body_html || "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur IA");
      setComposer((c) => c && { ...c, loading: false });
    }
  };

  const sendComposer = async () => {
    if (!composer) return;
    const { lead, to, subject, body_html, body_text } = composer;
    if (!to || !subject || !(body_html || body_text)) {
      toast.error("Email, sujet et contenu requis");
      return;
    }
    setComposer((c) => ({ ...c, sending: true }));
    try {
      const html = body_html || body_text.split("\n").map((l) => `<p>${l}</p>`).join("");
      await api.post("/admin/hot-leads/send-email", {
        to, subject, body_html: html, business_id: lead.business_id,
      });
      toast.success("Email envoyé au commerçant");
      setComposer(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi échoué");
      setComposer((c) => c && { ...c, sending: false });
    }
  };

  if (!data) return <div className="text-muted-foreground text-sm py-8">Chargement des leads chauds…</div>;

  const items = data.items || [];
  const totalIntents = items.reduce((a, b) => a + (b.intents || 0), 0);

  return (
    <div data-testid="admin-hot-leads">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <div className="label-uppercase mb-1 flex items-center gap-2"><Fire size={14} weight="fill" className="text-secondary" /> Leads chauds</div>
          <h2 className="font-display text-2xl font-medium">Commerçants Free qui reçoivent de l'intérêt</h2>
          <div className="text-xs text-muted-foreground mt-1">
            {items.length} fiche{items.length > 1 ? "s" : ""} · {totalIntents} intention{totalIntents > 1 ? "s" : ""} de contact sur les {data.window_days || days} derniers jours
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => { const d = Number(e.target.value); setDays(d); load(d); }}
            className="border border-border rounded-sm px-3 py-2 text-sm bg-background"
            data-testid="hot-leads-window"
          >
            <option value={7}>7 jours</option>
            <option value={14}>14 jours</option>
            <option value={30}>30 jours</option>
            <option value={90}>90 jours</option>
          </select>
          <button
            onClick={() => load(days)}
            disabled={busy}
            className="inline-flex items-center gap-1.5 border border-border hover:border-foreground px-3 py-2 text-sm rounded-sm disabled:opacity-50"
            data-testid="hot-leads-refresh"
          >
            <ArrowClockwise size={14} className={busy ? "animate-spin" : ""} />
            Rafraîchir
          </button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="hot-leads-stats">
          <div className="bg-card border border-border rounded-sm p-4">
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Emails envoyés · 30 j</div>
            <div className="font-display text-2xl font-medium mt-1">{stats.emails_sent}</div>
          </div>
          <div className="bg-card border border-border rounded-sm p-4">
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Commerçants contactés</div>
            <div className="font-display text-2xl font-medium mt-1">{stats.businesses_contacted}</div>
          </div>
          <div className="bg-card border border-border rounded-sm p-4">
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Activations</div>
            <div className="font-display text-2xl font-medium mt-1 text-primary">{stats.conversions}</div>
          </div>
          <div className={`rounded-sm p-4 ${stats.conversion_rate_pct >= 20 ? "bg-primary text-primary-foreground" : "bg-card border border-border"}`}>
            <div className={`text-[10px] uppercase tracking-widest font-bold ${stats.conversion_rate_pct >= 20 ? "opacity-80" : "text-muted-foreground"}`}>Taux de conversion</div>
            <div className="font-display text-2xl font-medium mt-1">{stats.conversion_rate_pct}%</div>
          </div>
        </div>
      )}

      {stats && stats.recent_wins && stats.recent_wins.length > 0 && (
        <div className="mb-6 bg-accent/40 border border-border rounded-sm px-4 py-3 text-sm">
          <span className="font-semibold">🏆 Dernières conversions : </span>
          {stats.recent_wins.slice(0, 5).map((w, i) => (
            <span key={w.slug} className="text-muted-foreground">
              {i > 0 ? " · " : ""}
              <a href={`/entreprise/${w.slug}`} target="_blank" rel="noreferrer" className="text-foreground hover:text-primary">
                {w.name}
              </a>
              {w.trial_used && !["premium", "premium_ai"].includes(w.tier) && <span className="text-[10px] text-muted-foreground"> (essai)</span>}
            </span>
          ))}
        </div>
      )}

      {items.length === 0 ? (
        <div className="bg-card border border-border rounded-sm p-8 text-center text-muted-foreground text-sm">
          Aucun lead chaud sur cette période. Les visiteurs signalent leur intérêt via le bouton « Signaler mon intérêt au commerçant » sur les fiches Free — ils apparaîtront ici.
        </div>
      ) : (
        <div className="overflow-x-auto bg-card border border-border rounded-sm">
          <table className="w-full text-sm">
            <thead className="bg-accent/40">
              <tr className="text-left">
                <th className="px-4 py-3 text-xs uppercase tracking-wider font-bold">Commerce</th>
                <th className="px-4 py-3 text-xs uppercase tracking-wider font-bold">Ville</th>
                <th className="px-4 py-3 text-xs uppercase tracking-wider font-bold text-center">Intents</th>
                <th className="px-4 py-3 text-xs uppercase tracking-wider font-bold">Statut</th>
                <th className="px-4 py-3 text-xs uppercase tracking-wider font-bold">Contact à relancer</th>
                <th className="px-4 py-3 text-xs uppercase tracking-wider font-bold">Dernière requête</th>
                <th className="px-4 py-3 text-xs uppercase tracking-wider font-bold text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.business_id} className="border-t border-border hover:bg-accent/20" data-testid={`hot-lead-row-${it.slug}`}>
                  <td className="px-4 py-3">
                    <a href={`/entreprise/${it.slug}`} target="_blank" rel="noreferrer" className="font-semibold hover:text-primary">
                      {it.name}
                    </a>
                    <div className="text-xs text-muted-foreground">{it.category || "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><MapPin size={12} /> {it.city || "—"}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${it.intents >= 5 ? "bg-destructive/15 text-destructive" : it.intents >= 2 ? "bg-secondary/20 text-secondary" : "bg-muted text-foreground"}`}>
                      <Fire size={12} weight="fill" /> {it.intents}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {it.claimed ? (
                      <span className="inline-flex items-center gap-1 text-xs text-primary font-semibold">
                        <Crown size={12} weight="fill" /> Réclamée
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">Non réclamée</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {it.claimed && it.owner_email ? (
                      <a href={`mailto:${it.owner_email}?subject=Vos%20leads%20Mon%20Commerce%2068`} className="text-primary hover:underline inline-flex items-center gap-1">
                        <EnvelopeSimple size={12} /> {it.owner_email}
                      </a>
                    ) : it.business_email ? (
                      <a href={`mailto:${it.business_email}`} className="text-muted-foreground hover:text-primary inline-flex items-center gap-1">
                        <EnvelopeSimple size={12} /> {it.business_email}
                      </a>
                    ) : it.business_phone ? (
                      <a href={`tel:${it.business_phone}`} className="text-muted-foreground hover:text-primary inline-flex items-center gap-1">
                        <Phone size={12} /> {it.business_phone}
                      </a>
                    ) : (
                      <span className="text-muted-foreground">Aucun contact</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground max-w-xs truncate" title={it.last_query || ""}>
                    {it.last_query ? `« ${it.last_query} »` : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => openComposer(it)}
                      disabled={!it.owner_email && !it.business_email}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                      data-testid={`hot-lead-ai-email-${it.slug}`}
                      title={!it.owner_email && !it.business_email ? "Aucun contact email" : "Générer un email IA personnalisé"}
                    >
                      <MagicWand size={12} weight="fill" /> Email IA
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {composer && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => !composer.sending && setComposer(null)} data-testid="composer-overlay">
          <div className="relative w-full max-w-2xl bg-card rounded-sm shadow-2xl ring-1 ring-border overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-border">
              <div>
                <div className="label-uppercase mb-1 text-primary">Email IA — {composer.lead.name}</div>
                <div className="text-xs text-muted-foreground">{composer.lead.city || "—"} · {composer.lead.intents} intent(s) cette semaine</div>
              </div>
              <button onClick={() => !composer.sending && setComposer(null)} className="w-8 h-8 grid place-items-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted" aria-label="Fermer">
                <X size={16} />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-1">Destinataire</div>
                <input
                  type="email"
                  value={composer.to}
                  onChange={(e) => setComposer({ ...composer, to: e.target.value })}
                  className="w-full border border-border rounded-sm px-3 py-2 text-sm bg-background outline-none focus:border-primary"
                  data-testid="composer-to"
                />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-1">Objet</div>
                <input
                  type="text"
                  value={composer.subject}
                  onChange={(e) => setComposer({ ...composer, subject: e.target.value })}
                  disabled={composer.loading}
                  className="w-full border border-border rounded-sm px-3 py-2 text-sm bg-background outline-none focus:border-primary disabled:opacity-50"
                  data-testid="composer-subject"
                />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-1">Message</div>
                {composer.loading ? (
                  <div className="text-center py-12 text-muted-foreground text-sm">
                    <MagicWand size={24} weight="duotone" className="mx-auto mb-2 animate-pulse" />
                    Génération IA en cours…
                  </div>
                ) : (
                  <textarea
                    value={composer.body_text}
                    onChange={(e) => setComposer({ ...composer, body_text: e.target.value, body_html: "" })}
                    rows={12}
                    className="w-full border border-border rounded-sm px-3 py-2 text-sm bg-background outline-none focus:border-primary font-mono leading-relaxed"
                    data-testid="composer-body"
                  />
                )}
                <div className="text-[11px] text-muted-foreground mt-1">Vous pouvez éditer librement avant d'envoyer.</div>
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-border bg-accent/30">
              <button
                onClick={() => !composer.sending && setComposer(null)}
                className="text-sm text-muted-foreground hover:text-foreground"
                disabled={composer.sending}
              >
                Annuler
              </button>
              <button
                onClick={sendComposer}
                disabled={composer.loading || composer.sending || !composer.to || !composer.subject}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-sm bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white text-sm font-semibold disabled:opacity-50"
                data-testid="composer-send"
              >
                <PaperPlaneTilt size={14} weight="fill" />
                {composer.sending ? "Envoi…" : "Envoyer l'email"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
